/**
 * Authentication interceptor utility
 * Handles automatic logout on token expiration (401/403 responses)
 */
import { clearEntitlements } from "./entitlements";
import { isPlanError, planErrorsOf } from "./plan-errors";

// Lazy import to avoid circular dependencies
let onLogoutFunction = null;
let apolloClientInstance = null;

function getApolloClient() {
  if (!apolloClientInstance && typeof window !== 'undefined') {
    // Dynamic import to avoid circular dependency
    const apolloModule = require("@/vue-apollo");
    apolloClientInstance = apolloModule.apolloClient;
  }
  return apolloClientInstance;
}

function getOnLogout() {
  if (!onLogoutFunction && typeof window !== 'undefined') {
    // Dynamic import to avoid circular dependency
    const apolloModule = require("@/vue-apollo");
    onLogoutFunction = apolloModule.onLogout;
  }
  return onLogoutFunction;
}

// Flag to prevent multiple simultaneous logout attempts
let isLoggingOut = false;

/**
 * Performs logout and clears all user data
 */
export function performLogout() {
  // Prevent multiple simultaneous logout calls
  if (isLoggingOut) {
    return;
  }
  
  isLoggingOut = true;
  
  try {
    // Clear Apollo token and reset store
    const onLogout = getOnLogout();
    const apolloClient = getApolloClient();
    onLogout(apolloClient);
    
    // Clear all user-related localStorage items
    localStorage.removeItem("apollo-done-token");
    localStorage.removeItem("DONE_USER");
    localStorage.removeItem("DONE_USER_ROLE");
    localStorage.removeItem("DONE_USER_PERMISSIONS");
    localStorage.removeItem("DONE_USER_ROLE_PERMISSIONS");
    clearEntitlements();
    
    // Redirect to login page
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }
  } catch (error) {
    console.error("Error during logout:", error);
  } finally {
    // Reset flag after a short delay to allow redirect
    setTimeout(() => {
      isLoggingOut = false;
    }, 1000);
  }
}

/**
 * Checks if a response indicates token expiration
 * @param {Response} response - The fetch response object
 * @returns {boolean} - True if the response indicates authentication failure
 */
export function isAuthError(response) {
  return response && (response.status === 401 || response.status === 403);
}

/**
 * Checks if an error indicates token expiration
 * @param {Error} error - The error object
 * @returns {boolean} - True if the error indicates authentication failure
 */
export function isAuthErrorFromGraphQL(error) {
  if (!error) return false;
  
  // Check for network errors with 401/403 status
  if (error.networkError) {
    const statusCode = error.networkError.statusCode;
    if (statusCode === 401 || statusCode === 403) {
      return true;
    }
  }
  
  // Plan-gate refusals (FEATURE_NOT_IN_PLAN / PLAN_LIMIT_REACHED / ACCOUNT_SUSPENDED,
  // SAAS-REVAMP/DESIGN.md §8.1) are never a session problem, whatever their text says: they open
  // the upgrade dialog instead. Leave them out of the keyword checks below.
  const planErrors = planErrorsOf(error);
  if (planErrors.length > 0 && planErrors.length === countGraphQLErrors(error)) {
    return false;
  }

  // Check for GraphQL errors indicating authentication failure
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const authErrors = error.graphQLErrors.filter(
      err => err.message && !isPlanError(err) && (
        err.message.toLowerCase().includes("unauthorized") ||
        err.message.toLowerCase().includes("forbidden") ||
        err.message.toLowerCase().includes("token") ||
        err.message.toLowerCase().includes("authentication")
      )
    );
    if (authErrors.length > 0) {
      return true;
    }
  }
  
  // Check error message for authentication-related keywords
  const errorMessage = error.message || "";
  if (
    errorMessage.toLowerCase().includes("unauthorized") ||
    errorMessage.toLowerCase().includes("forbidden") ||
    errorMessage.toLowerCase().includes("token expired") ||
    errorMessage.toLowerCase().includes("authentication")
  ) {
    return true;
  }
  
  return false;
}

/** GraphQL errors an Apollo error carries, including those in a 4xx response body. */
function countGraphQLErrors(error) {
  const body = error.networkError && error.networkError.result;
  return (error.graphQLErrors || []).length + (body && Array.isArray(body.errors) ? body.errors.length : 0);
}
